import React, { useState } from 'react';
import { 
    Container, 
    Typography, 
    Paper,
    Box,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';

const PrivacyPolicy = () => {
    const [language, setLanguage] = useState('es'); // Spanish as default

    const handleLanguageChange = (event, newLanguage) => {
        if (newLanguage !== null) {
            setLanguage(newLanguage);
        }
    };

    const content = {
        es: {
            title: 'Política de Privacidad – VideoCallShop',
            lastUpdated: 'Última actualización: 26 Nov 2025',
            welcome: 'Bienvenido a VideoCallShop ("App").',
            intro: 'Esta Política de Privacidad explica cómo recopilamos, utilizamos, divulgamos y protegemos la información cuando utiliza VideoCallShop y cualquier servicio relacionado (en conjunto, los "Servicios").',
            agreement: 'Al usar la App, acepta las prácticas descritas en esta Política de Privacidad. Si no está de acuerdo, por favor no use la App.',
            section1: '1. Quiénes somos',
            controller: 'Controlador / Responsable:',
            section2: '2. Información que recopilamos',
            section2Intro: 'Solo recopilamos información que es necesaria para operar VideoCallShop, brindar soporte al cliente y mejorar el servicio.',
            section21: '2.1 Información que usted proporciona',
            accountInfo: 'Información de cuenta:',
            accountInfoText: 'Nombre, dirección de correo electrónico, contraseña y otros detalles de registro cuando crea una cuenta.',
            profileInfo: 'Información de perfil (opcional):',
            profileInfoText: 'Foto de perfil, nombre para mostrar, preferencias, tiendas o productos guardados.',
            commContent: 'Contenido de comunicación:',
            commContentText: 'Mensajes que nos envía (correos de soporte, formularios) y mensajes de chat opcionales que envía a otros usuarios o comerciantes dentro de la app (si está disponible).',
            purchaseInfo: 'Información de compra:',
            purchaseInfoText: 'Detalles del pedido como productos comprados, precio, moneda, hora de compra y método de pago elegido.',
            purchaseNote: 'Nota: Los detalles de las tarjetas de pago son procesados por nuestros proveedores de pago y no son almacenados por VideoCallShop.',
            section22: '2.2 Información recopilada automáticamente',
            autoCollect: 'Cuando usa la App, podemos recopilar automáticamente:',
            deviceInfo: 'Información del dispositivo y registros:',
            deviceInfoText: 'Modelo del dispositivo, sistema operativo, versión de la app, dirección IP, idioma, zona horaria, registros de fallos y datos de rendimiento.',
            section23: '2.3 Cámara, micrófono y medios',
            cameraIntro: 'VideoCallShop utiliza videollamadas para conectar usuarios y comerciantes.',
            cameraMic: 'Cámara y micrófono:',
            cameraMicText1: 'Accedemos a la cámara y micrófono de su dispositivo solo cuando inicia o se une a una videollamada y únicamente para transmitir audio y video en tiempo real.',
            cameraMicText2: 'No los accedemos en segundo plano sin su conocimiento.',
            section24: '2.4 Información de terceros',
            thirdParty: 'Podemos recibir información de:',
            thirdPartyList: [
                'Proveedores de autenticación (p. ej. inicio de sesión de Google / Apple) – datos básicos del perfil que permite que compartan con nosotros.',
                'Proveedores de pago – confirmación de que un pago fue exitoso o falló.',
                'Servicios de análisis e informes de fallos – datos técnicos agregados para mejorar la estabilidad y el rendimiento.'
            ],
            section3: '3. Cómo utilizamos su información',
            useInfo: 'Utilizamos su información para los siguientes propósitos:',
            provideServices: 'Para proporcionar y operar los Servicios',
            provideServicesList: [
                'Crear y gestionar su cuenta.',
                'Habilitar videollamadas y comunicación entre usuarios y comerciantes.',
                'Procesar pedidos y pagos a través de nuestros socios de pago.'
            ],
            improveApp: 'Para mejorar y personalizar la App',
            improveAppList: [
                'Analizar el uso para entender qué funciones se usan más.',
                'Corregir errores y mejorar el rendimiento.',
                'Mostrar contenido relevante (por ejemplo, productos o comerciantes que puedan interesarle).'
            ],
            communicate: 'Para comunicarnos con usted',
            communicateList: [
                'Enviar correos electrónicos transaccionales o notificaciones (cambios de cuenta, actualizaciones de pedidos, alertas de seguridad).',
                'Responder a sus solicitudes de soporte.'
            ],
            safety: 'Para garantizar la seguridad y el cumplimiento legal',
            safetyList: [
                'Detectar y prevenir fraude, abuso o violaciones de nuestros Términos de Uso.',
                'Cumplir con obligaciones legales y responder a solicitudes legítimas de las autoridades.'
            ],
            consent: 'Cuando lo requiera la ley, confiamos en su consentimiento (por ejemplo, para comunicaciones de marketing opcionales) y puede retirar este consentimiento en cualquier momento.',
            section5: '5. Cómo compartimos su información',
            noSell: 'No vendemos su información personal.',
            shareCircumstances: 'Podemos compartir información en estas circunstancias limitadas:',
            serviceProviders: 'Proveedores de servicios',
            serviceProvidersText: 'Con terceros de confianza que realizan servicios para nosotros, tales como:',
            serviceProvidersList: [
                'Servicios de alojamiento en la nube y bases de datos',
                'Infraestructura de videollamadas',
                'Procesamiento de pagos',
                'Análisis e informes de fallos',
                'Entrega de correo electrónico y notificaciones'
            ],
            serviceProvidersNote: 'Estos proveedores solo procesan sus datos de acuerdo con nuestras instrucciones y para los propósitos descritos en esta Política.',
            merchants: 'Comerciantes y socios comerciales',
            merchantsText: 'Cuando interactúa con un comerciante específico en VideoCallShop (por ejemplo, durante una videollamada o al realizar un pedido), podemos compartir información relevante para que puedan cumplir con su pedido y brindar soporte al cliente (p. ej. nombre, datos de contacto, información de entrega, artículos comprados).',
            legal: 'Razones legales y de seguridad',
            legalText: 'Podemos divulgar información si creemos que es razonablemente necesario para:',
            legalList: [
                'Cumplir con la ley aplicable, proceso legal o solicitudes gubernamentales.',
                'Hacer cumplir nuestros Términos de Uso.',
                'Proteger nuestros derechos, propiedad o seguridad, o los derechos, propiedad o seguridad de otros.'
            ],
            businessTransfers: 'Transferencias comerciales',
            businessTransfersText: 'En relación con una fusión, adquisición, reestructuración o venta de activos, su información puede ser transferida como parte de la transacción, sujeto a esta Política de Privacidad.',
            section6: '6. Transferencias internacionales de datos',
            international1: 'Su información puede ser procesada y almacenada en servidores ubicados en países distintos al suyo.',
            international2: 'Cuando lo requiera la ley, implementamos salvaguardas apropiadas (como Cláusulas Contractuales Estándar) para proteger su información cuando se transfiere internacionalmente.',
            section7: '7. Retención de datos',
            retention: 'Mantenemos su información personal solo durante el tiempo necesario para los propósitos descritos en esta Política:',
            retentionList: [
                { label: 'Datos de cuenta', text: '– se mantienen mientras su cuenta esté activa.' },
                { label: 'Registros de pedidos y transacciones', text: '– se mantienen durante el período requerido por las leyes fiscales y contables.' },
                { label: 'Comunicaciones de soporte', text: '– se mantienen durante un período razonable para manejar problemas de seguimiento.' }
            ],
            retentionEnd: 'Cuando los datos ya no sean necesarios, los eliminaremos o anonimizaremos, a menos que la ley nos requiera mantenerlos por más tiempo.',
            section8: '8. Sus derechos',
            rights: 'Dependiendo de su ubicación, puede tener los siguientes derechos:',
            rightsList: [
                { label: 'Acceso', text: '– solicitar una copia de los datos personales que tenemos sobre usted.' },
                { label: 'Corrección', text: '– pedirnos que corrijamos datos inexactos o incompletos.' },
                { label: 'Eliminación', text: '– solicitar que eliminemos sus datos, en ciertas circunstancias.' },
                { label: 'Restricción', text: '– pedirnos que limitemos la forma en que usamos sus datos.' },
                { label: 'Portabilidad', text: '– recibir cierta información en un formato estructurado y legible por máquina.' },
                { label: 'Objeción', text: '– objetar al procesamiento basado en nuestros intereses legítimos o al marketing directo.' },
                { label: 'Retirar consentimiento', text: '– cuando el procesamiento se basa en su consentimiento.' }
            ],
            rightsContact: 'Para ejercer sus derechos, por favor contáctenos en privacy@videocallshop.com. Podemos solicitar prueba de identidad antes de responder a su solicitud.',
            rightsComplaint: 'Si está en el EEE/Reino Unido y cree que no estamos manejando sus datos de manera legal, también tiene derecho a presentar una queja ante su autoridad local de protección de datos.',
            section9: '9. Privacidad de menores',
            children1: 'VideoCallShop no está destinado para niños menores de 13 años (o la edad mínima en su país para usar servicios en línea).',
            children2: 'No recopilamos a sabiendas información personal de niños. Si nos enteramos de que un niño menor de esta edad nos ha proporcionado datos personales, los eliminaremos lo antes posible. Si cree que un niño nos ha proporcionado datos personales, por favor contáctenos.',
            section10: '10. Seguridad',
            security1: 'Utilizamos medidas técnicas y organizativas apropiadas para proteger su información personal, incluyendo cifrado en tránsito, controles de acceso y monitoreo de seguridad.',
            security2: 'Sin embargo, ningún método de transmisión o almacenamiento es completamente seguro, por lo que no podemos garantizar una seguridad absoluta.',
            section11: '11. Servicios y enlaces de terceros',
            thirdPartyLinks1: 'La App puede contener enlaces a sitios web o servicios de terceros (por ejemplo, proveedores de pago o redes sociales).',
            thirdPartyLinks2: 'No somos responsables de las prácticas de privacidad de esos terceros. Le recomendamos que lea sus políticas de privacidad.',
            section12: '12. Cambios a esta Política de Privacidad',
            changes1: 'Podemos actualizar esta Política de Privacidad de vez en cuando. Cuando lo hagamos, cambiaremos la fecha de "Última actualización" en la parte superior y, cuando corresponda, le notificaremos dentro de la App o por correo electrónico.',
            changes2: 'Su uso continuado de VideoCallShop después de cualquier cambio significa que acepta la Política de Privacidad actualizada.'
        },
        en: {
            title: 'Privacy Policy – VideoCallShop',
            lastUpdated: 'Last updated: 26 Nov 2025',
            welcome: 'Welcome to VideoCallShop ("App").',
            intro: 'This Privacy Policy explains how we collect, use, disclose and protect information when you use VideoCallShop and any related services (together, the "Services").',
            agreement: 'By using the App, you agree to the practices described in this Privacy Policy. If you do not agree, please do not use the App.',
            section1: '1. Who we are',
            controller: 'Controller / Responsible party:',
            section2: '2. Information we collect',
            section2Intro: 'We only collect information that is necessary to operate VideoCallShop, provide customer support and improve the service.',
            section21: '2.1 Information you provide',
            accountInfo: 'Account information:',
            accountInfoText: 'Name, email address, password and other registration details when you create an account.',
            profileInfo: 'Profile information (optional):',
            profileInfoText: 'Profile photo, display name, preferences, saved stores or products.',
            commContent: 'Communication content:',
            commContentText: 'Messages you send to us (support emails, forms) and optional chat messages you send to other users or merchants inside the app (if available).',
            purchaseInfo: 'Purchase information:',
            purchaseInfoText: 'Order details such as products purchased, price, currency, time of purchase and chosen payment method.',
            purchaseNote: 'Note: Payment card details are processed by our payment providers and are not stored by VideoCallShop.',
            section22: '2.2 Information collected automatically',
            autoCollect: 'When you use the App, we may automatically collect:',
            deviceInfo: 'Device and log information:',
            deviceInfoText: 'Device model, operating system, app version, IP address, language, time zone, crash logs and performance data.',
            section23: '2.3 Camera, microphone and media',
            cameraIntro: 'VideoCallShop uses video calls to connect users and merchants.',
            cameraMic: 'Camera and microphone:',
            cameraMicText1: 'We access your device\'s camera and microphone only when you start or join a video call and solely to transmit audio and video in real time.',
            cameraMicText2: 'We do not access them in the background without your knowledge.',
            section24: '2.4 Information from third parties',
            thirdParty: 'We may receive information from:',
            thirdPartyList: [
                'Authentication providers (e.g. Google / Apple sign-in) – basic profile data you allow them to share with us.',
                'Payment providers – confirmation that a payment was successful or failed.',
                'Analytics and crash reporting services – aggregated technical data to improve stability and performance.'
            ],
            section3: '3. How we use your information',
            useInfo: 'We use your information for the following purposes:',
            provideServices: 'To provide and operate the Services',
            provideServicesList: [
                'Create and manage your account.',
                'Enable video calls and communication between users and merchants.',
                'Process orders and payments through our payment partners.'
            ],
            improveApp: 'To improve and personalize the App',
            improveAppList: [
                'Analyse usage to understand which features are used most.',
                'Fix bugs and improve performance.',
                'Show relevant content (for example, products or merchants that may interest you).'
            ],
            communicate: 'To communicate with you',
            communicateList: [
                'Send transactional emails or notifications (account changes, order updates, security alerts).',
                'Respond to your support requests.'
            ],
            safety: 'To ensure safety, security and legal compliance',
            safetyList: [
                'Detect and prevent fraud, abuse, or violations of our Terms of Use.',
                'Comply with legal obligations and respond to lawful requests from authorities.'
            ],
            consent: 'Where required by law, we rely on your consent (for example for optional marketing communications) and you can withdraw this consent at any time.',
            section5: '5. How we share your information',
            noSell: 'We do not sell your personal information.',
            shareCircumstances: 'We may share information in these limited circumstances:',
            serviceProviders: 'Service providers',
            serviceProvidersText: 'With trusted third parties who perform services for us, such as:',
            serviceProvidersList: [
                'Cloud hosting and database services',
                'Video call infrastructure',
                'Payment processing',
                'Analytics and crash reporting',
                'Email and notification delivery'
            ],
            serviceProvidersNote: 'These providers only process your data according to our instructions and for the purposes described in this Policy.',
            merchants: 'Merchants and business partners',
            merchantsText: 'When you interact with a specific merchant in VideoCallShop (for example, during a video call or when placing an order), we may share relevant information so they can fulfil your order and provide customer support (e.g. name, contact details, delivery information, purchased items).',
            legal: 'Legal and safety reasons',
            legalText: 'We may disclose information if we believe it is reasonably necessary to:',
            legalList: [
                'Comply with applicable law, legal process or government requests.',
                'Enforce our Terms of Use.',
                'Protect our rights, property or safety, or the rights, property or safety of others.'
            ],
            businessTransfers: 'Business transfers',
            businessTransfersText: 'In connection with a merger, acquisition, restructuring or sale of assets, your information may be transferred as part of the transaction, subject to this Privacy Policy.',
            section6: '6. International data transfers',
            international1: 'Your information may be processed and stored on servers located in countries other than your own.',
            international2: 'Where required by law, we implement appropriate safeguards (such as Standard Contractual Clauses) to protect your information when it is transferred internationally.',
            section7: '7. Data retention',
            retention: 'We keep your personal information only for as long as necessary for the purposes described in this Policy:',
            retentionList: [
                { label: 'Account data', text: '– kept while your account is active.' },
                { label: 'Order and transaction records', text: '– kept for the period required by tax and accounting laws.' },
                { label: 'Support communications', text: '– kept for a reasonable period to handle follow-up issues.' }
            ],
            retentionEnd: 'When data is no longer needed, we will delete or anonymize it, unless we are required by law to keep it longer.',
            section8: '8. Your rights',
            rights: 'Depending on your location, you may have the following rights:',
            rightsList: [
                { label: 'Access', text: '– request a copy of the personal data we hold about you.' },
                { label: 'Correction', text: '– ask us to correct inaccurate or incomplete data.' },
                { label: 'Deletion', text: '– request that we delete your data, in certain circumstances.' },
                { label: 'Restriction', text: '– ask us to limit the way we use your data.' },
                { label: 'Portability', text: '– receive certain information in a structured, machine-readable format.' },
                { label: 'Objection', text: '– object to processing based on our legitimate interests or to direct marketing.' },
                { label: 'Withdraw consent', text: '– when processing is based on your consent.' }
            ],
            rightsContact: 'To exercise your rights, please contact us at privacy@videocallshop.com. We may ask for proof of identity before responding to your request.',
            rightsComplaint: 'If you are in the EEA/UK and believe we are not handling your data lawfully, you also have the right to lodge a complaint with your local data protection authority.',
            section9: '9. Children\'s privacy',
            children1: 'VideoCallShop is not intended for children under 13 (or the minimum age in your country for using online services).',
            children2: 'We do not knowingly collect personal information from children. If we learn that a child under this age has provided us with personal data, we will delete it as soon as possible. If you believe a child has provided us with personal data, please contact us.',
            section10: '10. Security',
            security1: 'We use appropriate technical and organizational measures to protect your personal information, including encryption in transit, access controls and security monitoring.',
            security2: 'However, no method of transmission or storage is completely secure, so we cannot guarantee absolute security.',
            section11: '11. Third-party services and links',
            thirdPartyLinks1: 'The App may contain links to third-party websites or services (for example, payment providers or social networks).',
            thirdPartyLinks2: 'We are not responsible for the privacy practices of those third parties. We encourage you to read their privacy policies.',
            section12: '12. Changes to this Privacy Policy',
            changes1: 'We may update this Privacy Policy from time to time. When we do, we will change the "Last updated" date at the top and, where appropriate, notify you inside the App or by email.',
            changes2: 'Your continued use of VideoCallShop after any changes means you accept the updated Privacy Policy.'
        }
    };

    const t = content[language];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flex: 1, minWidth: '200px' }}>
                        {t.title}
                    </Typography>
                    <ToggleButtonGroup
                        value={language}
                        exclusive
                        onChange={handleLanguageChange}
                        aria-label="language selection"
                        size="small"
                    >
                        <ToggleButton value="es" aria-label="español">
                            ES
                        </ToggleButton>
                        <ToggleButton value="en" aria-label="english">
                            EN
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    {t.lastUpdated}
                </Typography>

                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                    {t.welcome}
                </Typography>

                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                    {t.intro}
                </Typography>

                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                    {t.agreement}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section1}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.controller}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    VideoCallShop<br />
                    privacy@videocallshop.com
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section2}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.section2Intro}
                </Typography>

                <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                    {t.section21}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.accountInfo}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.accountInfoText}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.profileInfo}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.profileInfoText}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.commContent}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.commContentText}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.purchaseInfo}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.purchaseInfoText}
                </Typography>
                <Typography variant="body2" paragraph sx={{ ml: 2, fontStyle: 'italic' }}>
                    {t.purchaseNote}
                </Typography>

                <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                    {t.section22}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.autoCollect}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.deviceInfo}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.deviceInfoText}
                </Typography>

                <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                    {t.section23}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.cameraIntro}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.cameraMic}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.cameraMicText1}
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.cameraMicText2}
                </Typography>

                <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                    {t.section24}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.thirdParty}
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 2 }}>
                    {t.thirdPartyList.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section3}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.useInfo}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.provideServices}</strong>
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 2, mb: 2 }}>
                    {t.provideServicesList.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.improveApp}</strong>
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 2, mb: 2 }}>
                    {t.improveAppList.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.communicate}</strong>
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 2, mb: 2 }}>
                    {t.communicateList.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.safety}</strong>
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 2, mb: 2 }}>
                    {t.safetyList.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.consent}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section5}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.noSell}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.shareCircumstances}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.serviceProviders}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.serviceProvidersText}
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 4, mb: 2 }}>
                    {t.serviceProvidersList.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.serviceProvidersNote}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.merchants}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.merchantsText}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.legal}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.legalText}
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 4, mb: 2 }}>
                    {t.legalList.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>

                <Typography variant="body1" paragraph>
                    <strong>{t.businessTransfers}</strong>
                </Typography>
                <Typography variant="body1" paragraph sx={{ ml: 2 }}>
                    {t.businessTransfersText}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section6}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.international1}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.international2}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section7}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.retention}
                </Typography>

                <Typography variant="body1" component="ul" sx={{ ml: 2, mb: 2 }}>
                    {t.retentionList.map((item, index) => (
                        <li key={index}><strong>{item.label}</strong> {item.text}</li>
                    ))}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.retentionEnd}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section8}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.rights}
                </Typography>

                <Typography variant="body1" component="ul" sx={{ ml: 2, mb: 2 }}>
                    {t.rightsList.map((item, index) => (
                        <li key={index}><strong>{item.label}</strong> {item.text}</li>
                    ))}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.rightsContact}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.rightsComplaint}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section9}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.children1}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.children2}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section10}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.security1}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.security2}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section11}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.thirdPartyLinks1}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.thirdPartyLinks2}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.section12}
                </Typography>

                <Typography variant="body1" paragraph>
                    {t.changes1}
                </Typography>

                <Typography variant="body1" paragraph sx={{ mb: 0 }}>
                    {t.changes2}
                </Typography>
            </Paper>
        </Container>
    );
};

export default PrivacyPolicy;

